<template>
  <div>
    <el-form
      ref="form"
      :model="formValue"
      :rules="formRules"
      @status="handleFormStatusChange"
    >
      <el-row :gutter="24">
        <el-col
          :span="12"
        >
          <el-form-item prop="firstName">
            <el-input
              v-model="formValue.firstName"
              :label="$t('consumer-profile.first-name')"
              required
            />
          </el-form-item>
        </el-col>
        <el-col
          :span="12"
        >
          <el-form-item
            prop="lastName"
            :inline-message="false"
          >
            <el-input
              v-model="formValue.lastName"
              :label="$t('consumer-profile.last-name')"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="login">
            <el-input
              v-model="formValue.login"
              :label="$t('consumer-profile.login')"
              disabled
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="email">
            <el-input
              v-model="formValue.email"
              :label="$t('consumer-profile.email')"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="12">
          <el-form-item prop="password">
            <el-input
              v-model="formValue.password"
              :label="$t('consumer-profile.password')"
              readonly
            >
              <template #suffix>
                <el-link
                  @click="openResetPasswordDialog"
                >
                  {{ "Change" }}
                </el-link>
              </template>
            </el-input>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="phone">
            <el-input
              v-model="formValue.phone"
              :label="$t('consumer-profile.phone')"
              prefix-value="+"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col :span="hasStates ? 12 : 24">
          <el-form-item prop="country">
            <el-select
              v-model="formValue.country"
              :label="$t('consumer-profile.country')"
              filterable
              @change="handleCountryChange"
            >
              <el-option
                v-for="country in countries"
                :key="country.code"
                :value="country.code"
                :label="country.name"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col
          v-if="hasStates"
          :span="12"
        >
          <el-form-item prop="state">
            <el-select
              v-model="formValue.state"
              :label="$t('consumer-profile.state')"
              filterable
            >
              <el-option
                v-for="state in states"
                :key="state"
                :label="state"
                :value="state"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col
          :span="12"
        >
          <el-form-item prop="city">
            <el-input
              v-model="formValue.city"
              :label="$t('consumer-profile.city')"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item prop="zipCode">
            <el-input
              v-model="formValue.zipCode"
              :label="$t('consumer-profile.zip-code')"
            />
          </el-form-item>
        </el-col>
      </el-row>
      <el-row :gutter="24">
        <el-col
          :span="12"
        >
          <el-form-item prop="address">
            <el-input
              v-model="formValue.address"
              :label="$t('consumer-profile.address')"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <reset-password-dialog
      v-if="isResetPasswordDialogShown"
      :user="value"
      @hide="isResetPasswordDialogShown = false"
    />
  </div>
</template>

<script>
import { getCountries, getStates, hasStates } from '@/app/library/country-state-picker';
import { isEmailValid } from '@/app/library/isEmailValid';
import { isEmpty } from '@/app/library/isEmpty';
import ResetPasswordDialog from './ResetPasswordDialog.vue';

export default {
  name: 'ConsumerProfileForm',
  components: { ResetPasswordDialog },
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'input',
    'status'
  ],
  data() {
    return {
      formValue: this.convertFormValueToProxy(this.getDefaultFormValue()),
      formRules: {
        firstName: [
          {
            required: true,
            message: this.$t('form-error.required', { field: this.$t('consumer-profile.first-name') })
          }
        ],
        email: [
          {
            validator: this.validateEmail
          }
        ],
        phone: [
          {
            pattern: /^[0-9+\-#\s()]+$/,
            message: this.$t('form-error.phone.incorrect')
          }
        ],
        country: [
          {
            required: true,
            message: this.$t('form-error.required', { field: this.$t('consumer-profile.country') })
          }
        ],
        state: [
          {
            required: true,
            message: this.$t('form-error.required', { field: this.$t('consumer-profile.state') })
          }
        ]
      },
      countries: getCountries(),
      isResetPasswordDialogShown: false
    };
  },
  computed: {
    states() {
      return this.formValue.country ? getStates(this.formValue.country) : [];
    },
    hasStates() {
      return hasStates(this.formValue.country);
    }
  },
  created() {
    this.setFormValue(this.value);
  },
  async mounted() {
    await this.$nextTick();
    this.$refs.form.clearValidate();
  },
  methods: {
    handleFormStatusChange(status) {
      this.$emit('status', status);
    },
    handleCountryChange(country) {
      if (!hasStates(country)) {
        this.formValue.state = '';
      }
    },
    convertFormValueToProxy(formValue) {
      const self = this;

      return new Proxy(formValue, {
        get(target, prop) {
          return target[prop];
        },
        set(target, prop, value) {
          target[prop] = value;
          self.$emit('input', target);

          return true;
        }
      });
    },
    getDefaultFormValue() {
      return {
        firstName: undefined,
        lastName: undefined,
        login: undefined,
        email: undefined,
        password: '**************',
        phone: '',
        country: '',
        state: '',
        city: undefined,
        zipCode: undefined,
        address: undefined
      };
    },
    setFormValue(value) {
      Object.keys(value).forEach((key) => {
        if (!isEmpty(value[key])) {
          this.formValue[key] = value[key];
        }
      });
    },
    validateEmail(_, value, callback) {
      if (!value) {
        callback(new Error(this.$t('form-error.required', { field: this.$t('consumer-profile.email') })));
      } else if (!isEmailValid(value)) {
        callback(new Error(this.$t('form-error.email.incorrect')));
      } else {
        callback();
      }
    },
    clearValidate() {
      this.$refs.form.clearValidate();
    },
    async validate() {
      try {
        return await this.$refs.form.validate();
      } catch (e) {
        return false;
      }
    },
    openResetPasswordDialog() {
      this.isResetPasswordDialogShown = true;
    }
  }
};
</script>
